import React from 'react'
import './LeagueInfoSection.css'

/**
 * LeagueInfoSection - Compact inline explanation of how ServeSync Tennis works
 *
 * Condensed version for the landing page with collapsible detailed information.
 *
 * Props:
 * - collapsed: boolean - whether the section is collapsed (controlled by parent)
 * - onToggle: function - callback when expand/collapse state changes
 */
export default function LeagueInfoSection({ collapsed = false, onToggle }) {
  const handleToggle = () => {
    if (onToggle) {
      onToggle(!collapsed)
    }
  }

  return (
    <div className="league-info-section">
      {/* Compact header with key message */}
      <div className="lis-header">
        <div className="lis-header-content">
          <span className="lis-icon" aria-hidden="true">
            🎾
          </span>
          <div className="lis-header-text">
            <h3 className="lis-title">Play Anyone, Anytime</h3>
            <p className="lis-subtitle">
              No fixed weekly matches—play any opponent when it works for both
              of you
            </p>
          </div>
        </div>
        <button
          className="lis-toggle"
          onClick={handleToggle}
          aria-label={collapsed ? 'Learn how it works' : 'Hide details'}
          aria-expanded={!collapsed}
        >
          {collapsed ? 'Learn How' : 'Hide'}
        </button>
      </div>

      {/* Collapsible content - condensed */}
      {!collapsed && (
        <div className="lis-content">
          {/* Key difference callout */}
          <div className="lis-key-difference">
            You're not assigned specific opponents each week. Play{' '}
            <strong>anyone in your division</strong>, whenever your schedules
            align.
          </div>

          {/* 4-step process in compact cards */}
          <div className="lis-steps-compact">
            <div className="lis-step-card">
              <div className="lis-step-icon">📅</div>
              <div className="lis-step-title">Share Times</div>
              <div className="lis-step-desc">Post availability</div>
            </div>
            <div className="lis-step-divider">→</div>
            <div className="lis-step-card">
              <div className="lis-step-icon">🔍</div>
              <div className="lis-step-title">Propose</div>
              <div className="lis-step-desc">Pick opponent/time</div>
            </div>
            <div className="lis-step-divider">→</div>
            <div className="lis-step-card">
              <div className="lis-step-icon">✅</div>
              <div className="lis-step-title">Accept</div>
              <div className="lis-step-desc">They confirm</div>
            </div>
            <div className="lis-step-divider">→</div>
            <div className="lis-step-card">
              <div className="lis-step-icon">🎾</div>
              <div className="lis-step-title">Play!</div>
              <div className="lis-step-desc">You're scheduled</div>
            </div>
          </div>

          {/* Key benefits - compact */}
          <div className="lis-benefits-compact">
            <div className="lis-benefit-item">
              <span className="lis-benefit-icon">⚡</span>
              <span>Play at your own pace</span>
            </div>
            <div className="lis-benefit-item">
              <span className="lis-benefit-icon">🤝</span>
              <span>Mutual agreement required</span>
            </div>
            <div className="lis-benefit-item">
              <span className="lis-benefit-icon">📊</span>
              <span>Standings track all matches</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
